import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, User, Briefcase, Users as UsersIcon, Plus, LogIn, Edit2 } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const AdminUserApprovals = () => {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [editForm, setEditForm] = useState({ email: '', user_type: '', password: '' });
  const [adminForm, setAdminForm] = useState({ email: '', password: '' });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (userId, status) => {
    try {
      await api.put(`/admin/users/${userId}/approval`, { approval_status: status });
      toast.success(`User ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchUsers();
      setShowDetails(false);
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const viewDetails = async (user) => {
    try {
      const response = await api.get(`/admin/users/${user.id}/details`);
      setSelectedUser(response.data);
      setShowDetails(true);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleEdit = (user) => {
    setEditForm({
      email: user.email,
      user_type: user.user_type || '',
      password: ''
    });
    setSelectedUser(user);
    setShowEdit(true);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/users/${selectedUser.id}`, editForm);
      toast.success('User updated successfully');
      fetchUsers();
      setShowEdit(false);
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleLoginAs = async (userId) => {
    try {
      const response = await api.post(`/admin/login-as/${userId}`);
      const { access_token, user } = response.data;
      
      // Store the token and reload to login as this user
      localStorage.setItem('token', access_token);
      toast.success(`Logging in as ${user.email}`);
      
      // Reload the page to update context
      setTimeout(() => {
        window.location.href = user.role === 'admin' ? '/admin' : '/admin/dashboard';
      }, 500);
    } catch (error) {
      toast.error('Failed to login as user');
    }
  };

  const createAdmin = async () => {
    if (!adminForm.email || !adminForm.password) {
      toast.error('Email and password are required');
      return;
    }
    
    try {
      await api.post('/admin/create-admin', adminForm);
      toast.success('Admin user created successfully');
      setShowCreateAdmin(false);
      setAdminForm({ email: '', password: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-amber-100', text: 'text-amber-800', icon: Clock, label: 'Pending' },
      approved: { bg: 'bg-emerald-100', text: 'text-emerald-800', icon: CheckCircle, label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Rejected' }
    };

    const style = styles[status] || styles.pending;
    const Icon = style.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 ${style.bg} ${style.text} px-3 py-1 rounded-full text-xs font-medium`}>
        <Icon className="w-3.5 h-3.5" />
        {style.label}
      </span>
    );
  };

  const getUserTypeIcon = (type) => {
    return type === 'broker' ? <Briefcase className="w-4 h-4" /> : <UsersIcon className="w-4 h-4" />;
  };

  const filteredUsers = filter === 'all'
    ? users
    : users.filter(u => u.approval_status === filter);

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-900"></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-100">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold text-slate-800">User Approvals</h1>
              <button
                onClick={() => setShowCreateAdmin(true)}
                className="bg-purple-600 text-white hover:bg-purple-700 px-4 py-2 rounded-md font-medium inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Admin User
              </button>
            </div>
            
            {/* Filters */}
            <div className="flex gap-2 flex-wrap">
              {['pending', 'approved', 'rejected', 'all'].map((status) => (
                <button
                  key={status}
                  data-testid={`filter-${status}`}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    filter === status
                      ? 'bg-emerald-900 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                data-testid={`user-${user.id}`}
                className="p-6 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Name/Email</label>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <div>
                          {user.company_name && <p className="font-medium text-slate-800">{user.company_name}</p>}
                          <p className="text-sm text-slate-600">{user.email}</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-slate-500 uppercase">Role/Type</label>
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium w-fit ${
                          user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-700'
                        }`}>
                          {user.role === 'admin' ? '👑 Admin' : 'User'}
                        </span>
                        {user.user_type && (
                          <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-1 rounded text-xs w-fit">
                            {getUserTypeIcon(user.user_type)}
                            {user.user_type.charAt(0).toUpperCase() + user.user_type.slice(1)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-slate-500 uppercase">Contact</label>
                      <p className="text-sm text-slate-700">{user.phone || 'N/A'}</p>
                      <p className="text-xs text-slate-500">{user.country || 'N/A'}</p>
                    </div>

                    <div>
                      <label className="text-xs text-slate-500 uppercase">Last IP</label>
                      <p className="text-sm font-mono text-slate-700">{user.last_login_ip || 'Never logged'}</p>
                      <p className="text-xs text-slate-500">{user.login_count || 0} logins</p>
                    </div>

                    <div>
                      <label className="text-xs text-slate-500 uppercase">Status</label>
                      <div className="mt-1">{getStatusBadge(user.approval_status)}</div>
                    </div>

                    <div>
                      <label className="text-xs text-slate-500 uppercase">Registered</label>
                      <p className="text-xs text-slate-600">{new Date(user.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    {user.role !== 'admin' && (
                      <button
                        onClick={() => handleLoginAs(user.id)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 font-medium px-3 py-1 rounded hover:bg-indigo-50"
                        title="Login as this user"
                      >
                        Login As
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(user)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium px-3 py-1 rounded hover:bg-blue-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => viewDetails(user)}
                      className="text-sm text-emerald-900 hover:text-emerald-700 font-medium px-3 py-1 rounded hover:bg-emerald-50"
                    >
                      View Details
                    </button>
                    
                    {user.approval_status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApproval(user.id, 'approved')}
                          data-testid={`approve-${user.id}`}
                          className="bg-emerald-600 text-white hover:bg-emerald-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleApproval(user.id, 'rejected')}
                          data-testid={`reject-${user.id}`}
                          className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {filteredUsers.length === 0 && (
              <div className="p-12 text-center text-slate-500">
                No users found for this filter.
              </div>
            )}
          </div>
        </div>

        {/* User Details Modal */}
        {showDetails && selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50" onClick={() => setShowDetails(false)}>
            <div className="bg-white rounded-xl max-w-2xl w-full p-8 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-800">User Details</h2>
                <button onClick={() => setShowDetails(false)} className="text-slate-400 hover:text-slate-600">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600">Email</label>
                    <p className="text-slate-800 font-medium">{selectedUser.email}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-600">User Type</label>
                    <p className="text-slate-800">{selectedUser.user_type || 'Not specified'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-600">Status</label>
                    <div className="mt-1">{getStatusBadge(selectedUser.approval_status)}</div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-600">Login Count</label>
                    <p className="text-slate-800">{selectedUser.login_count || 0} logins</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-600">Last Login</label>
                    <p className="text-slate-800 text-sm">
                      {selectedUser.last_login_at 
                        ? new Date(selectedUser.last_login_at).toLocaleString()
                        : 'Never'}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-600">Last Login IP</label>
                    <p className="text-slate-800 font-mono text-sm">{selectedUser.last_login_ip || 'N/A'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-600">Created At</label>
                    <p className="text-slate-800 text-sm">
                      {new Date(selectedUser.created_at).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-600">Last Modified</label>
                    <p className="text-slate-800 text-sm">
                      {selectedUser.updated_at 
                        ? new Date(selectedUser.updated_at).toLocaleString()
                        : 'N/A'}
                    </p>
                  </div>

                  {selectedUser.approved_at && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-slate-600">Approved At</label>
                        <p className="text-slate-800 text-sm">
                          {new Date(selectedUser.approved_at).toLocaleString()}
                        </p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-slate-600">Approved By</label>
                        <p className="text-slate-800 text-sm">Admin #{selectedUser.approved_by}</p>
                      </div>
                    </>
                  )}
                </div>

                {selectedUser.answers && selectedUser.answers.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-slate-600 mb-2 block">Submitted Information</label>
                    <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                      {selectedUser.answers.map((answer, idx) => (
                        <div key={idx}>
                          <p className="text-sm font-medium text-slate-700">{answer.question_text}</p>
                          <p className="text-slate-600">{answer.answer_value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedUser.activities && selectedUser.activities.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-slate-600 mb-2 block">Recent Activity (Last 50)</label>
                    <div className="bg-slate-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <div className="space-y-2">
                        {selectedUser.activities.map((activity, idx) => (
                          <div key={idx} className="text-sm border-b border-slate-200 pb-2 last:border-0">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-slate-700">{activity.action_type}</span>
                              <span className="text-xs text-slate-500">
                                {new Date(activity.created_at).toLocaleString()}
                              </span>
                            </div>
                            {activity.action_detail && (
                              <p className="text-slate-600 text-xs mt-1">{activity.action_detail}</p>
                            )}
                            {activity.ip_address && (
                              <p className="text-slate-500 font-mono text-xs">IP: {activity.ip_address}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {selectedUser.approval_status === 'pending' && (
                  <div className="flex gap-3 pt-4 border-t">
                    <button
                      onClick={() => handleApproval(selectedUser.id, 'approved')}
                      className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700 px-4 py-3 rounded-md font-medium"
                    >
                      Approve User
                    </button>
                    <button
                      onClick={() => handleApproval(selectedUser.id, 'rejected')}
                      className="flex-1 bg-red-600 text-white hover:bg-red-700 px-4 py-3 rounded-md font-medium"
                    >
                      Reject User
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEdit && selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50" onClick={() => setShowEdit(false)}>
            <div className="bg-white rounded-xl max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-bold text-slate-800 mb-6">Edit User</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-emerald-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">User Type</label>
                  <select
                    value={editForm.user_type}
                    onChange={(e) => setEditForm({ ...editForm, user_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-emerald-900"
                  >
                    <option value="">Not Set</option>
                    <option value="broker">Broker</option>
                    <option value="buyer">Buyer</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    New Password (leave blank to keep current)
                  </label>
                  <input
                    type="password"
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                    placeholder="Enter new password"
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-emerald-900"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={saveEdit}
                    className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700 px-4 py-3 rounded-md font-medium"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => setShowEdit(false)}
                    className="flex-1 bg-slate-200 text-slate-700 hover:bg-slate-300 px-4 py-3 rounded-md font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Admin Modal */}
        {showCreateAdmin && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50" onClick={() => setShowCreateAdmin(false)}>
            <div className="bg-white rounded-xl max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-bold text-slate-800 mb-6">Create Admin User</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={adminForm.email}
                    onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                    placeholder="admin@example.com"
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-emerald-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                  <input
                    type="password"
                    value={adminForm.password}
                    onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                    placeholder="Secure password"
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-emerald-900"
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> Admin users have full access to all features including user management, question configuration, and LOI approvals.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={createAdmin}
                    className="flex-1 bg-purple-600 text-white hover:bg-purple-700 px-4 py-3 rounded-md font-medium"
                  >
                    Create Admin
                  </button>
                  <button
                    onClick={() => setShowCreateAdmin(false)}
                    className="flex-1 bg-slate-200 text-slate-700 hover:bg-slate-300 px-4 py-3 rounded-md font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminUserApprovals;
